import { useState, useEffect } from 'react'

export const useGridUndoRedo = (gridApiRef) => {
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [canValidate, setCanValidate] = useState(false)

  // Update canValidate when undoStack changes
  useEffect(() => {
    setCanValidate(undoStack.length > 0)
  }, [undoStack])

  const recordEdit = (rowNumbers, oldValues, setValueCallback) => {
    if (!gridApiRef?.current) {
      return
    }

    const rowNodes = rowNumbers.map((rowNumber) => gridApiRef.current.getRowNode(rowNumber))
    setUndoStack([...undoStack, { rowNodes, oldValues, setValueCallback }])
    setRedoStack([])
    setCanValidate(true)
  }

  const undo = () => {
    if (!gridApiRef?.current) {
      return
    }

    if (undoStack.length > 0) {
      const lastAction = undoStack.pop()
      setUndoStack([...undoStack])
      setRedoStack([...redoStack, lastAction])

      // Apply the transaction and refresh *after* it completes
      gridApiRef.current.applyTransactionAsync({ update: lastAction.oldValues }, (result) => {
        gridApiRef.current.refreshCells({
          force: true,
          rowNodes: lastAction.rowNodes,
        })
      })

      // Stop editing immediately (can happen outside the callback)
      gridApiRef.current.stopEditing()

      // Update canValidate based on remaining undo actions
      setCanValidate(undoStack.length > 0)
    }
  }

  const redo = () => {
    if (!gridApiRef?.current) {
      return
    }

    if (redoStack.length > 0) {
      const lastAction = redoStack.pop()
      setRedoStack([...redoStack])
      setUndoStack([...undoStack, lastAction])

      // Call the improved setValueCallback that uses transactions
      lastAction.setValueCallback()

      // Stop editing is still done directly
      gridApiRef.current.stopEditing()

      // If we redo an action, we can validate again
      setCanValidate(true)
    }
  }

  const resetValidation = () => {
    // Reset validation state after validate button is clicked
    setCanValidate(false)
  }

  const clearUndoRedo = () => {
    setUndoStack([])
    setRedoStack([])
    setCanValidate(false)
  }

  return {
    undo,
    redo,
    recordEdit,
    clearUndoRedo,
    resetValidation,
    undoSize: undoStack.length,
    redoSize: redoStack.length,
    canValidate,
  }
}
